var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, svg, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
export const PROVIDER_NAME = {
    anthropic: 'Anthropic',
    openai: 'OpenAI',
    google: 'Google',
    xai: 'xAI',
    openrouter: 'OpenRouter',
    deepseek: 'DeepSeek',
    qwen: 'Qwen',
    meta: 'Meta',
    mistral: 'Mistral',
    nvidia: 'NVIDIA',
    moonshot: 'Moonshot',
    tencent: 'Tencent',
    poolside: 'Poolside',
    cohere: 'Cohere',
    zai: 'Z.ai',
    groq: 'Groq',
    perplexity: 'Perplexity',
    microsoft: 'Microsoft',
    amazon: 'Amazon',
    nousresearch: 'Nous Research',
    ai21: 'AI21',
    baidu: 'Baidu',
    bytedance: 'ByteDance',
    minimax: 'MiniMax',
    reka: 'Reka',
    inflection: 'Inflection',
    liquid: 'Liquid',
    cognitivecomputations: 'Cognitive Computations',
    movendi: 'Movendi',
    custom: 'Custom',
};
/** Display name for any vendor key — mapped when known, Title-cased as a sane fallback. */
export function providerName(key) {
    return PROVIDER_NAME[key] ?? (key ? key.charAt(0).toUpperCase() + key.slice(1) : 'Provider');
}
/** Best-effort provider inference from a raw model id (fallback for BYO / unknown ids). */
export function providerOf(model) {
    const m = model.toLowerCase();
    // Vendor-namespaced ids (`vendor/model[:free]`) group under their BRAND (the prefix,
    // alias-normalized) — checked FIRST so `google/gemma-…:free` is Google, not a gemini/other
    // substring match. Mirrors the worker's vendorKeyOf.
    if (m.includes('/')) {
        const prefix = m.slice(0, m.indexOf('/'));
        const alias = { 'meta-llama': 'meta', mistralai: 'mistral', moonshotai: 'moonshot', 'x-ai': 'xai', 'z-ai': 'zai' };
        return alias[prefix] ?? prefix;
    }
    if (m.includes('claude') || m.includes('anthropic'))
        return 'anthropic';
    if (m.startsWith('gpt') || m.includes('openai') || m.startsWith('o1') || m.startsWith('o3') || m.startsWith('o4'))
        return 'openai';
    if (m.includes('gemini') || m.includes('google'))
        return 'google';
    if (m.includes('grok') || m.includes('xai'))
        return 'xai';
    return 'custom';
}
let logoBase = '';
/** Point every <vitesse-provider-logo> at the app's logo endpoint (set once at boot). */
export function setProviderLogoBase(base) {
    logoBase = base.replace(/\/$/, '');
}
// One fetch per provider per session, shared by every instance.
const logoCache = new Map();
function loadLogo(provider) {
    if (!logoBase)
        return Promise.resolve(null);
    const key = `${logoBase}/${provider}.svg`;
    let p = logoCache.get(key);
    if (!p) {
        // cache: 'reload' — go to the network, bypassing the browser HTTP cache. A logo's SVG at a
        // given URL CAN change (a mono→color swap, a corrected mark), and an earlier `immutable`
        // cache header meant the browser would never re-fetch it — so an updated logo never reached
        // an open tab. The in-memory logoCache above already dedupes to one fetch per session, so
        // this costs nothing extra; it just guarantees the fresh mark.
        p = fetch(key, { cache: 'reload' })
            .then((r) => (r.ok ? r.text() : null))
            .catch(() => null);
        logoCache.set(key, p);
    }
    return p;
}
let VitesseProviderLogo = class VitesseProviderLogo extends LitElement {
    constructor() {
        super(...arguments);
        this.provider = 'custom';
        this.size = 18;
        this.svgText = null;
    }
    static { this.styles = css `
    :host {
      display: inline-flex;
      line-height: 0;
    }
    span {
      display: inline-flex;
      line-height: 0;
    }
    svg {
      display: block;
    }
  `; }
    willUpdate(changed) {
        if (changed.has('provider')) {
            this.svgText = null;
            const wanted = this.provider;
            void loadLogo(wanted).then((text) => {
                if (this.provider === wanted)
                    this.svgText = text; // ignore stale loads after a prop change
            });
        }
    }
    render() {
        // Backend SVGs are width/height=1em → size via font-size. The endpoint is our own
        // worker (trusted, same-origin); inlining keeps currentColor theming working.
        if (this.svgText)
            return html `<span style="font-size:${this.size}px">${unsafeHTML(this.svgText)}</span>`;
        // Fallback (endpoint unset / unreachable / loading): a generic connector glyph.
        return html `<svg width=${this.size} height=${this.size} viewBox="0 0 24 24" role="img" aria-label=${providerName(this.provider)}>
      ${svg `<g fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" opacity="0.75"><rect x="3" y="7.5" width="14.5" height="9" rx="2" /><path d="M17.5 10.5h3.5M17.5 13.5h3.5" /></g>`}
    </svg>`;
    }
};
__decorate([
    property()
], VitesseProviderLogo.prototype, "provider", void 0);
__decorate([
    property({ type: Number })
], VitesseProviderLogo.prototype, "size", void 0);
__decorate([
    state()
], VitesseProviderLogo.prototype, "svgText", void 0);
VitesseProviderLogo = __decorate([
    customElement('vitesse-provider-logo')
], VitesseProviderLogo);
export { VitesseProviderLogo };
//# sourceMappingURL=vitesse-provider-logo.js.map